import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Personal } from '../types';
import { User, Crown, Zap, Users } from 'lucide-react';
import { useMsal } from '@azure/msal-react';
import { loadUserPhotos } from '../services/photoService';

interface OrganigramaProps {
  personal: Personal[];
  areaColor?: string;
}

const Organigrama: React.FC<OrganigramaProps> = ({ personal, areaColor = '#3B82F6' }) => {
  const [userPhotos, setUserPhotos] = useState<Record<string, string>>({});
  const [loadingProgress, setLoadingProgress] = useState<{ loaded: number; total: number } | null>(null);
  const { instance, accounts } = useMsal();
  const account = accounts[0];
  
  // Ref para evitar múltiples cargas
  const isLoadingPhotos = useRef(false);
  const loadedPersonalIds = useRef<Set<number>>(new Set());

  // Organizar personal por roles jerárquicos
  const directores = personal.filter(p => p.rol.toLowerCase().includes('director') || p.rol.toLowerCase().includes('head of'));
  const teamLeaders = personal.filter(p => p.rol.toLowerCase() === 'team leader');
  const consultores = personal.filter(p => p.rol.toLowerCase() === 'consultor');

  // Función optimizada para cargar fotos con 1 sola autenticación
  const loadPhotos = useCallback(async () => {
    if (!account || isLoadingPhotos.current) return;
    
    // Verificar si ya cargamos fotos para este grupo
    const currentPersonalIds = new Set(personal.map(p => p.id));
    const alreadyLoaded = Array.from(currentPersonalIds).every(id => loadedPersonalIds.current.has(id));
    
    if (alreadyLoaded) return;

    isLoadingPhotos.current = true;
    
    try {
      console.log('🔐 Obteniendo token de acceso...');
      
      // Obtener token una sola vez
      const response = await instance.acquireTokenSilent({
        scopes: ['https://graph.microsoft.com/User.Read.All'],
        account: account,
      });
      
      console.log('✅ Token obtenido, iniciando carga de fotos...');
      
      // Filtrar emails válidos
      const emails = personal
        .filter(p => p.email && p.email.includes('@'))
        .map(p => p.email!);
      
      if (emails.length === 0) {
        console.log('📧 No hay emails válidos para cargar fotos');
        isLoadingPhotos.current = false;
        return;
      }
      
      // Cargar fotos con progreso
      const photos = await loadUserPhotos(
        emails,
        response.accessToken,
        (loaded, total) => setLoadingProgress({ loaded, total })
      );
      
      // Actualizar estado con fotos cargadas
      setUserPhotos(prev => ({ ...prev, ...photos }));
      
      // Marcar como cargado
      currentPersonalIds.forEach(id => loadedPersonalIds.current.add(id));
      
      console.log(`🎉 Fotos del organigrama cargadas: ${Object.keys(photos).length}/${emails.length}`);
      
    } catch (error) {
      console.error('❌ Error cargando fotos:', error);
    } finally {
      isLoadingPhotos.current = false;
      setLoadingProgress(null);
    }
  }, [personal, account, instance]);

  // Cargar fotos cuando cambie el personal
  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  const getInitials = (nombre: string, apellido?: string) => {
    return `${nombre.charAt(0)}${apellido ? apellido.charAt(0) : ''}`.toUpperCase();
  };

  const getRoleIcon = (rol: string) => {
    const roleLower = rol.toLowerCase();
    if (roleLower.includes('head of') || roleLower.includes('director')) {
      return Crown;
    }
    switch (roleLower) {
      case 'team leader':
        return Zap;
      case 'consultor':
        return User;
      default:
        return Users;
    }
  };

  const getRoleColor = (rol: string) => {
    const roleLower = rol.toLowerCase();
    if (roleLower.includes('head of') || roleLower.includes('director')) {
      return 'border-primary bg-primary/5 dark:bg-primary/10';
    }
    switch (roleLower) {
      case 'team leader':
        return 'border-neutral-300 dark:border-neutral-600 bg-neutral-50 dark:bg-neutral-800';
      case 'consultor':
        return 'border-neutral-300 dark:border-neutral-600 bg-neutral-50 dark:bg-neutral-800';
      default:
        return 'border-neutral-300 dark:border-neutral-600 bg-neutral-50 dark:bg-neutral-800';
    }
  };

  const PersonCard: React.FC<{ person: Personal; isLeader?: boolean }> = ({ 
    person, 
    isLeader = false 
  }) => {
    const Icon = getRoleIcon(person.rol);
    
    return (
      <div className={`
        relative p-4 rounded-lg border-2 shadow-sm hover:shadow-md transition-all
        ${getRoleColor(person.rol)}
        ${isLeader ? 'min-w-48' : 'min-w-40'}
      `}>
        {/* Avatar */}
        <div className="flex flex-col items-center mb-3">
          <div className="w-12 h-12 rounded-full mb-2 overflow-hidden">
            {person.email && userPhotos[person.email] ? (
              <img 
                src={userPhotos[person.email]} 
                alt={`${person.nombre} ${person.apellido || ''}`}
                className="w-full h-full object-cover"
              />
            ) : (
              <div 
                className="w-full h-full flex items-center justify-center text-white font-semibold"
                style={{ backgroundColor: areaColor }}
              >
                {getInitials(person.nombre, person.apellido)}
              </div>
            )}
          </div>
          
          {/* Role Icon */}
          <div className="absolute top-2 right-2">
            <Icon size={16} className="text-neutral-600 dark:text-neutral-400" />
          </div>
        </div>

        {/* Info */}
        <div className="text-center">
          <h4 className="font-semibold text-neutral-900 dark:text-neutral-50 text-sm">
            {person.nombre} {person.apellido}
          </h4>
          <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-1">
            {(person.rol.toLowerCase().includes('director') || person.rol.toLowerCase().includes('head of')) && person.area
              ? `Head of ${person.area.nombre}`
              : person.rol}
          </p>
          
          {/* Status */}
          <div className="flex items-center justify-center mt-2">
            <div className={`w-2 h-2 rounded-full mr-2 ${
              person.disponible ? 'bg-green-400' : 'bg-red-400'
            }`}></div>
            <span className="text-xs text-neutral-500 dark:text-neutral-400">
              {person.disponible ? 'Disponible' : 'Ocupado'}
            </span>
          </div>

          {/* Email */}
          {person.email && (
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 truncate">
              {person.email}
            </p>
          )}
        </div>
      </div>
    );
  };

  const ConnectionLine: React.FC<{ vertical?: boolean; horizontal?: boolean }> = ({
    vertical = false,
    horizontal = false
  }) => (
    <div
      className={`${
        vertical ? 'h-8 w-1 mx-auto' : horizontal ? 'w-8 h-1 my-auto' : 'w-4 h-1'
      } bg-neutral-400 dark:bg-neutral-500 rounded-full`}
    />
  );

  if (personal.length === 0) {
    return (
      <div className="text-center py-8 text-neutral-600 dark:text-neutral-400">
        <Users size={48} className="mx-auto mb-4 text-neutral-300 dark:text-neutral-600" />
        <p>No hay personal asignado a esta área</p>
      </div>
    );
  }

  return (
    <div className="organigrama p-6 bg-neutral-50 dark:bg-neutral-900 rounded-lg">
      {/* Indicador de progreso de carga de fotos */}
      {loadingProgress && (
        <div className="mb-4 bg-white dark:bg-neutral-800 rounded-lg p-3 shadow-sm border border-neutral-200 dark:border-neutral-700">
          <div className="flex items-center justify-between text-sm text-neutral-600 dark:text-neutral-400 mb-2">
            <span>Cargando fotos de perfil...</span>
            <span>{loadingProgress.loaded}/{loadingProgress.total}</span>
          </div>
          <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2">
            <div
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{
                width: `${(loadingProgress.loaded / loadingProgress.total) * 100}%`
              }}
            />
          </div>
        </div>
      )}
      
      <div className="flex flex-col items-center space-y-6">
        
        {/* Directores */}
        {directores.length > 0 && (
          <div className="flex flex-col items-center">
            <div className="flex justify-center space-x-4">
              {directores.map(director => (
                <PersonCard key={director.id} person={director} isLeader />
              ))}
            </div>
            
            {(teamLeaders.length > 0 || consultores.length > 0) && (
              <ConnectionLine vertical />
            )}
          </div>
        )}

        {/* Team Leaders */}
        {teamLeaders.length > 0 && (
          <div className="flex flex-col items-center">
            <div className="flex justify-center space-x-4">
              {teamLeaders.map(teamLeader => (
                <PersonCard key={teamLeader.id} person={teamLeader} />
              ))}
            </div>
            
            {consultores.length > 0 && (
              <ConnectionLine vertical />
            )}
          </div>
        )}

        {/* Consultores */}
        {consultores.length > 0 && (
          <div className="flex flex-col items-center">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-4xl">
              {consultores.map(consultor => (
                <PersonCard key={consultor.id} person={consultor} />
              ))}
            </div>
          </div>
        )}

        {/* Estadísticas del equipo */}
        <div className="mt-8 w-full max-w-2xl">
          <div className="card p-4">
            <h4 className="font-semibold text-neutral-900 dark:text-neutral-50 mb-3 text-center">
              Resumen del Equipo
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-primary">
                  {directores.length}
                </div>
                <div className="text-xs text-neutral-600 dark:text-neutral-400">Heads of Area</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-neutral-700 dark:text-neutral-300">
                  {teamLeaders.length}
                </div>
                <div className="text-xs text-neutral-600 dark:text-neutral-400">Team Leaders</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-neutral-700 dark:text-neutral-300">
                  {consultores.length}
                </div>
                <div className="text-xs text-neutral-600 dark:text-neutral-400">Consultores</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-success">
                  {personal.filter(p => p.disponible).length}
                </div>
                <div className="text-xs text-neutral-600 dark:text-neutral-400">Disponibles</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Organigrama;